/* eslint-disable react-hooks/exhaustive-deps -- effects intentionally run on mount / on the listed deps; adding the flagged functions risks render loops. */
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { usersAPI } from '../services/api';
import { User, UserPrivacySettings } from '../types';

interface ProfileProps {
  userId?: string;
  currentUserId: string;
  isOwnProfile?: boolean;
}

const Profile: React.FC<ProfileProps> = ({ 
  userId, 
  currentUserId, 
  isOwnProfile = false 
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [privacySettings, setPrivacySettings] = useState<UserPrivacySettings | null>(null);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<User>>({});
  const [privacyFormData, setPrivacyFormData] = useState<Partial<UserPrivacySettings>>({});
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fullBodyInputRef = useRef<HTMLInputElement>(null);

  const targetUserId = userId || currentUserId;

  const handleUploadFullBody = async (file: File) => {
    setUploadingPhoto(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await usersAPI.uploadFullBodyPhoto(fd);
      setUser((prev) => (prev ? { ...prev, full_body_photo_url: res.data.full_body_photo_url } : prev));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleRemoveFullBody = async () => {
    setUploadingPhoto(true);
    try {
      await usersAPI.deleteFullBodyPhoto();
      setUser((prev) => (prev ? { ...prev, full_body_photo_url: undefined } : prev));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to remove photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  useEffect(() => {
    loadUserData();
  }, [targetUserId, currentUserId]);

  const loadUserData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [userResponse, statsResponse] = await Promise.all([
        usersAPI.getProfile(targetUserId),
        usersAPI.getUserStats(targetUserId)
      ]);

      setUser(userResponse.data);
      setStats(statsResponse.data);
      setFormData(userResponse.data);

      if (isOwnProfile) {
        const privacyResponse = await usersAPI.getPrivacySettings();
        setPrivacySettings(privacyResponse.data);
        setPrivacyFormData(privacyResponse.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      await usersAPI.updateProfile(formData);

      if (isOwnProfile && privacySettings) {
        await usersAPI.updatePrivacySettings(privacyFormData);
      }

      setUser({ ...user!, ...formData });
      if (privacySettings) {
        setPrivacySettings({ ...privacySettings, ...privacyFormData });
      }
      setEditing(false);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData(user || {});
    setPrivacyFormData(privacySettings || {});
    setEditing(false);
    setError(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Alert severity="error">User not found</Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        <Box display="flex" alignItems="center" mb={3}>
          <Avatar
            src={user.avatar_url}
            sx={{ width: 80, height: 80, mr: 2 }}
          >
            {user.display_name.charAt(0).toUpperCase()}
          </Avatar>
          
          <Box flexGrow={1}>
            {editing ? (
              <TextField
                fullWidth
                label="Display Name"
                value={formData.display_name || ''}
                onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                sx={{ mb: 1 }}
              />
            ) : (
              <Typography variant="h4">{user.display_name}</Typography>
            )}
            
            <Typography variant="body2" color="textSecondary">
              @{user.username}
            </Typography>
            
            {user.location && (
              <Typography variant="body2" color="textSecondary">
                📍 {user.location}
              </Typography>
            )}
          </Box>

          {isOwnProfile && (
            <Box>
              {editing ? (
                <>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={saving}
                    sx={{ mr: 1 }}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={() => setEditing(true)}
                >
                  Edit Profile
                </Button>
              )}
            </Box>
          )}
        </Box>

        {editing ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Bio"
              multiline
              rows={3}
              value={formData.bio || ''}
              onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="City"
                value={formData.city || ''}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                placeholder="e.g., San Francisco"
              />
              <TextField
                fullWidth
                label="State"
                value={formData.state || ''}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                placeholder="e.g., CA"
              />
            </Box>
            <TextField
              fullWidth
              label="Website"
              value={formData.website || ''}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
            />
            <TextField
              fullWidth
              label="Style Preferences"
              multiline
              rows={2}
              value={formData.style_preferences || ''}
              onChange={(e) => setFormData({ ...formData, style_preferences: e.target.value })}
              placeholder="e.g., Minimalist, Streetwear, Preppy..."
            />
            {isOwnProfile && (
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_public || false}
                    onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })}
                  />
                }
                label="Make profile public (others can see your name, city/state, style, and top 5 items)"
              />
            )}
          </Box>
        ) : (
          <>
            {user.bio && (
              <Typography variant="body1" sx={{ mb: 2 }}>
                {user.bio}
              </Typography>
            )}

            {(user.city || user.state) && (
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                📍 {[user.city, user.state].filter(Boolean).join(', ')}
              </Typography>
            )}

            {user.style_preferences && (
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                ✨ Style: {user.style_preferences}
              </Typography>
            )}

            {user.website && (
              <Typography variant="body2" color="primary">
                🌐 <a href={user.website} target="_blank" rel="noopener noreferrer">
                  {user.website}
                </a>
              </Typography>
            )}
          </>
        )}

        <Divider sx={{ my: 3 }} />

        {stats && (
          <Box>
            <Typography variant="h6" gutterBottom>Stats</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
              <Box textAlign="center">
                <Typography variant="h4">{stats.wardrobe_items}</Typography>
                <Typography variant="body2" color="textSecondary">Items</Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="h4">{stats.outfits}</Typography>
                <Typography variant="body2" color="textSecondary">Outfits</Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="h4">{stats.friends}</Typography>
                <Typography variant="body2" color="textSecondary">Friends</Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="h4">{stats.collections}</Typography>
                <Typography variant="body2" color="textSecondary">Collections</Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Paper>

      {(user.top_5_items && user.top_5_items.length > 0) && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Top 5 Items</Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            These are the items I feel best in
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
            {user.top_5_items.map((itemId: any, index: number) => (
              <Box
                key={index}
                sx={{
                  width: 100,
                  height: 100,
                  border: '2px solid #e0e0e0',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'grey.50'
                }}
              >
                <Typography variant="caption" color="textSecondary">
                  Item {typeof itemId === 'object' ? itemId.id : itemId}
                </Typography>
              </Box>
            ))}
          </Box>
          {isOwnProfile && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Top 5 item selection will be available soon. For now, you can manage this from your wardrobe by marking items as favorites.
            </Alert>
          )}
        </Paper>
      )}

      {isOwnProfile && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Full-Body Photo</Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Add a clear, full-body photo of yourself. The assistant uses it to show you wearing outfit ideas.
          </Typography>
          <input
            ref={fullBodyInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleUploadFullBody(f);
              e.target.value = '';
            }}
          />
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, mt: 2, flexWrap: 'wrap' }}>
            <Box
              sx={{
                width: 150, height: 200, borderRadius: 2, border: '2px dashed #c9c9c9',
                bgcolor: 'grey.50', overflow: 'hidden', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
              }}
            >
              {user.full_body_photo_url ? (
                <Box component="img" src={user.full_body_photo_url} alt="Full-body"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <Typography variant="caption" color="textSecondary" sx={{ p: 1, textAlign: 'center' }}>
                  No photo yet
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                variant="contained"
                disabled={uploadingPhoto}
                onClick={() => fullBodyInputRef.current?.click()}
              >
                {uploadingPhoto ? 'Uploading…' : user.full_body_photo_url ? 'Replace photo' : 'Upload photo'}
              </Button>
              {user.full_body_photo_url && (
                <Button color="inherit" disabled={uploadingPhoto} onClick={handleRemoveFullBody}>
                  Remove
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      )}

      {isOwnProfile && privacySettings && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Privacy Settings</Typography>
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Profile Visibility</InputLabel>
            <Select
              value={editing ? privacyFormData.profile_visibility || 'public' : privacySettings.profile_visibility}
              onChange={(e) => setPrivacyFormData({ 
                ...privacyFormData, 
                profile_visibility: e.target.value as 'public' | 'friends' | 'private'
              })}
              disabled={!editing}
            >
              <MenuItem value="public">Public</MenuItem>
              <MenuItem value="friends">Friends Only</MenuItem>
              <MenuItem value="private">Private</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
            {[
              { key: 'share_wardrobe', label: 'Share Wardrobe Items' },
              { key: 'share_outfits', label: 'Share Outfits' },
              { key: 'share_collections', label: 'Share Collections' },
              { key: 'share_trips', label: 'Share Trips' },
              { key: 'share_activity', label: 'Share Activity' },
              { key: 'share_calendar', label: 'Share Calendar' }
            ].map(({ key, label }) => (
              <FormControlLabel
                key={key}
                control={
                  <Switch
                    checked={editing 
                      ? privacyFormData[key as keyof UserPrivacySettings] as boolean
                      : privacySettings[key as keyof UserPrivacySettings] as boolean
                    }
                    onChange={(e) => setPrivacyFormData({
                      ...privacyFormData,
                      [key]: e.target.checked
                    })}
                    disabled={!editing}
                  />
                }
                label={label}
              />
            ))}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default Profile;